import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useConfirm } from "material-ui-confirm";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { AppAutocomplete, AppButton, AppCheckbox, AppIconButton, AppTextField } from "../../../components/form";
import { AppActionsRow, AppFormField, AppFormGrid, AppSlideOver, PageSectionCard } from "../../../components/layout";
import PageEmptyState from "../../../components/Page/PageEmptyState";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import { isConfirmCancelled } from "../../../hooks/useDeleteConfirm";
import {
  customerPlanService,
  type CustomerDetail,
  type CustomerSummary,
  type SupportEntitlement,
  type SupportPackage,
} from "../../../services/customerPlanService";
import { QuotaFieldsEditor, QuotaSummary, formToQuota, quotaToForm, type QuotaForm } from "./quotaFields";

const PAGE_SIZE = 25;
type DetailTab = "quotas" | "support" | "history";

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : "—");

/** yyyy-MM-dd for <input type="date"> from an ISO instant. */
const toDateInput = (iso?: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
/** Start of the given day (UTC) as ISO, or null. */
const fromDateInput = (value: string) => (value ? new Date(`${value}T00:00:00Z`).toISOString() : null);

function ResourceChips({ c }: { c: CustomerSummary }) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        variant="outlined"
        color={c.beasLlmInUse ? "primary" : "default"}
        label={t(c.beasLlmInUse ? "superAdmin.customers.llmBeas" : "superAdmin.customers.llmOwn")}
      />
      <Chip
        size="small"
        variant="outlined"
        color={c.beasStoreInUse ? "primary" : "default"}
        label={t(c.beasStoreInUse ? "superAdmin.customers.storeBeas" : "superAdmin.customers.storeOwn")}
      />
    </Stack>
  );
}

function remainingText(e: SupportEntitlement, t: (k: string, o?: Record<string, unknown>) => string) {
  const parts: string[] = [];
  if (e.tickets != null) parts.push(t("superAdmin.customers.ticketsLeft", { left: e.ticketsRemaining ?? 0, total: e.tickets }));
  if (e.meetingHours != null)
    parts.push(t("superAdmin.customers.minutesLeft", { left: e.meetingMinutesRemaining ?? 0, total: e.meetingHours * 60 }));
  return parts.join(" · ");
}

/**
 * Customers = workspaces: which BEAS resources they use (Free quotas apply only then), custom quotas,
 * and support packages with their payments and use.
 */
export default function CustomersPanel() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<CustomerSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<SupportPackage[]>([]);

  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState<DetailTab>("quotas");
  const [saving, setSaving] = useState(false);

  // custom quota form
  const [quotaForm, setQuotaForm] = useState<QuotaForm>(quotaToForm(null));
  const [quotaUnlimited, setQuotaUnlimited] = useState(false);
  const [quotaFrom, setQuotaFrom] = useState("");
  const [quotaUntil, setQuotaUntil] = useState("");
  const [quotaNote, setQuotaNote] = useState("");

  // support assignment form
  const [assignKey, setAssignKey] = useState<string>("");
  const [assignStart, setAssignStart] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [custom, setCustom] = useState({ name: "", tickets: "", hours: "", price: "", install: "", term: "12", ticketResponse: "", meetingResponse: "" });

  // usage / payment dialogs
  const [usageFor, setUsageFor] = useState<SupportEntitlement | null>(null);
  const [usageKind, setUsageKind] = useState<"TICKET" | "MEETING">("TICKET");
  const [usageAmount, setUsageAmount] = useState("1");
  const [usageRef, setUsageRef] = useState("");
  const [usageNote, setUsageNote] = useState("");
  const [paymentFor, setPaymentFor] = useState<SupportEntitlement | null>(null);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [amount, setAmount] = useState("");

  const load = useCallback(
    async (q: string, p: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await customerPlanService.listCustomers(q, p, PAGE_SIZE);
        setRows(res.items || []);
        setTotal(res.total || 0);
      } catch {
        setError(t("superAdmin.customers.loadError"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    void load("", 0);
    customerPlanService
      .listSupportPackages()
      .then((list) => setCatalog(Array.isArray(list) ? list : []))
      .catch(() => setCatalog([]));
  }, [load]);

  const loadDetail = useCallback(
    async (id: string) => {
      setDetailLoading(true);
      try {
        const d = await customerPlanService.getCustomer(id);
        setDetail(d);
        const cq = d.customQuota;
        setQuotaForm(quotaToForm(cq?.quotas));
        setQuotaUnlimited(!!cq?.unlimited);
        setQuotaFrom(toDateInput(cq?.validFrom));
        setQuotaUntil(toDateInput(cq?.validUntil));
        setQuotaNote(cq?.note || "");
      } catch {
        setError(t("superAdmin.customers.detailError"));
      } finally {
        setDetailLoading(false);
      }
    },
    [t]
  );

  const open = useCallback(
    (id: string) => {
      setOpenId(id);
      setTab("quotas");
      setDetail(null);
      setError(null);
      setAssignKey("");
      void loadDetail(id);
    },
    [loadDetail]
  );

  // Deep link from Super Admin → Users: ?workspace=<id>
  useEffect(() => {
    const ws = searchParams.get("workspace");
    if (ws && ws !== openId) open(ws);
  }, [searchParams, open, openId]);

  const close = () => {
    if (saving) return;
    setOpenId(null);
    setDetail(null);
    if (searchParams.get("workspace")) {
      searchParams.delete("workspace");
      setSearchParams(searchParams, { replace: true });
    }
    void load(search, page);
  };

  const run = async (fn: () => Promise<unknown>, okKey: string) => {
    if (!openId) return;
    setSaving(true);
    setError(null);
    try {
      await fn();
      setSuccess(t(okKey));
      await loadDetail(openId);
      return true;
    } catch (e: any) {
      setError(e?.response?.data?.response?.message || e?.response?.data?.message || t("superAdmin.customers.actionError"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveQuota = () =>
    run(
      () =>
        customerPlanService.setQuotaOverride(openId!, {
          unlimited: quotaUnlimited,
          quotas: formToQuota(quotaForm),
          validFrom: fromDateInput(quotaFrom),
          validUntil: fromDateInput(quotaUntil),
          note: quotaNote.trim() || null,
        }),
      "superAdmin.customers.quotaSaved"
    );

  const removeQuota = async () => {
    try {
      await confirm({
        title: t("superAdmin.customers.removeQuotaTitle"),
        description: t("superAdmin.customers.removeQuotaDescription"),
        confirmationText: t("superAdmin.customers.removeQuota"),
        cancellationText: t("common.cancel"),
      });
    } catch (e) {
      if (isConfirmCancelled(e)) return;
      throw e;
    }
    await run(() => customerPlanService.removeQuotaOverride(openId!), "superAdmin.customers.quotaRemoved");
  };

  const isCustom = assignKey === "CUSTOM";
  const numOrNull = (v: string) => (v.trim() && Number.isFinite(Number(v)) ? Number(v) : null);
  const assign = async () => {
    const ok = await run(
      () =>
        customerPlanService.assignSupport(openId!, {
          packageKey: assignKey,
          startsAt: fromDateInput(assignStart),
          note: assignNote.trim() || null,
          ...(isCustom
            ? {
                name: custom.name.trim() || null,
                tickets: numOrNull(custom.tickets),
                meetingHours: numOrNull(custom.hours),
                priceEur: numOrNull(custom.price),
                installFeeEur: numOrNull(custom.install),
                termMonths: numOrNull(custom.term),
                ticketResponse: custom.ticketResponse.trim() || null,
                meetingResponse: custom.meetingResponse.trim() || null,
              }
            : {}),
        }),
      "superAdmin.customers.supportAssigned"
    );
    if (ok) {
      setAssignKey("");
      setAssignStart("");
      setAssignNote("");
    }
  };

  const recordUsage = async () => {
    if (!usageFor) return;
    const n = Number(usageAmount);
    if (!Number.isFinite(n) || n <= 0) return;
    const ok = await run(
      () =>
        customerPlanService.recordSupportUsage(openId!, usageFor.id, {
          kind: usageKind,
          amount: Math.floor(n),
          reference: usageRef.trim() || undefined,
          note: usageNote.trim() || undefined,
        }),
      "superAdmin.customers.usageRecorded"
    );
    if (ok) setUsageFor(null);
  };

  const recordPayment = async () => {
    if (!paymentFor) return;
    const n = Number(amount);
    if (!invoiceNo.trim() || !Number.isFinite(n) || n <= 0) return;
    const ok = await run(
      () => customerPlanService.recordSupportPayment(openId!, paymentFor.id, invoiceNo.trim(), n),
      "superAdmin.customers.paymentRecorded"
    );
    if (ok) setPaymentFor(null);
  };

  const cancelSupport = async (e: SupportEntitlement) => {
    try {
      await confirm({
        title: t("superAdmin.customers.cancelSupportTitle", { name: e.packageName }),
        description: t("superAdmin.customers.cancelSupportDescription"),
        confirmationText: t("superAdmin.customers.cancelSupport"),
        cancellationText: t("common.cancel"),
        confirmationButtonProps: { color: "error", variant: "contained" },
      });
    } catch (err) {
      if (isConfirmCancelled(err)) return;
      throw err;
    }
    await run(() => customerPlanService.cancelSupport(openId!, e.id), "superAdmin.customers.supportCancelled");
  };

  const packageOptions = useMemo(
    () => catalog.filter((p) => p.active || p.key === "CUSTOM").map((p) => ({ id: p.key, name: p.name })),
    [catalog]
  );
  const c = detail?.customer;

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {error && !openId && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && !openId && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <PageSectionCard title={t("superAdmin.customers.title")} description={t("superAdmin.customers.description")} noPadding={rows.length > 0}>
        <Box sx={{ px: rows.length > 0 ? 1.5 : 0, pt: rows.length > 0 ? 1.5 : 0 }}>
          <AppActionsRow align="start">
            <AppTextField
              size="small"
              label={t("superAdmin.customers.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(0);
                  void load(search, 0);
                }
              }}
              sx={{ minWidth: 260 }}
            />
            <AppButton
              variant="outlined"
              disabled={loading}
              onClick={() => {
                setPage(0);
                void load(search, 0);
              }}
            >
              {t("common.search")}
            </AppButton>
          </AppActionsRow>
        </Box>
        {loading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={26} />
          </Stack>
        ) : rows.length === 0 ? (
          <PageEmptyState message={t("superAdmin.customers.empty")} />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("superAdmin.customers.workspace")}</TableCell>
                  <TableCell>{t("superAdmin.customers.owner")}</TableCell>
                  <TableCell>{t("superAdmin.customers.resources")}</TableCell>
                  <TableCell>{t("superAdmin.customers.quota")}</TableCell>
                  <TableCell>{t("superAdmin.customers.support")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.workspaceId} hover sx={{ cursor: "pointer" }} onClick={() => open(row.workspaceId)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.ownerUsername || "—"}</TableCell>
                    <TableCell>
                      <ResourceChips c={row} />
                    </TableCell>
                    <TableCell>
                      {row.customQuotaActive ? (
                        <Chip
                          size="small"
                          color="secondary"
                          label={
                            row.customQuotaUnlimited
                              ? t("superAdmin.customers.customUnlimited", { date: fmtDate(row.customQuotaUntil) })
                              : t("superAdmin.customers.customUntil", { date: fmtDate(row.customQuotaUntil) })
                          }
                        />
                      ) : row.beasLlmInUse || row.beasStoreInUse ? (
                        <Chip size="small" variant="outlined" label={t("superAdmin.customers.free")} />
                      ) : (
                        <Chip size="small" variant="outlined" color="success" label={t("superAdmin.customers.noCaps")} />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.activeSupport.length === 0 ? (
                        "—"
                      ) : (
                        <Stack spacing={0.25}>
                          {row.activeSupport.map((e) => (
                            <Typography key={e.id} variant="caption">
                              <b>{e.packageName}</b> · {remainingText(e, t)}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_, p) => {
                setPage(p);
                void load(search, p);
              }}
            />
          </>
        )}
      </PageSectionCard>

      <AppSlideOver open={!!openId} onClose={close} size="xl" title={c ? c.name : t("superAdmin.customers.title")}>
        {error && openId && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && openId && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {detailLoading && !detail ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        ) : detail && c ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                {t("superAdmin.customers.ownerLine", { owner: c.ownerUsername || "—" })}
              </Typography>
              <ResourceChips c={c} />
              <Chip size="small" color="info" label={t(`superAdmin.customers.source.${detail.quotaSource}`)} />
            </Stack>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tab value="quotas" label={t("superAdmin.customers.tabQuotas")} />
              <Tab value="support" label={t("superAdmin.customers.tabSupport")} />
              <Tab value="history" label={t("superAdmin.customers.tabHistory")} />
            </Tabs>

            {tab === "quotas" && (
              <Stack spacing={3}>
                <Alert severity="info">{t("superAdmin.customers.effectiveHint")}</Alert>
                <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={650} sx={{ mb: 1 }}>
                      {t("superAdmin.customers.effective")}
                    </Typography>
                    <QuotaSummary values={detail.effectiveQuotas} noCapLabel={t("superAdmin.plans.noCap")} dense />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={650} sx={{ mb: 1 }}>
                      {t("superAdmin.customers.freeValues")}
                    </Typography>
                    <QuotaSummary values={detail.freeQuotas} noCapLabel={t("superAdmin.plans.noCap")} dense />
                  </Box>
                </Box>
                <Divider />
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={650}>
                      {t("superAdmin.customers.customQuota")}
                    </Typography>
                    {detail.customQuota && (
                      <Chip
                        size="small"
                        color={detail.customQuota.active ? "secondary" : "default"}
                        label={detail.customQuota.active ? t("superAdmin.customers.active") : t("superAdmin.customers.notActive")}
                      />
                    )}
                  </Stack>
                  {detail.customQuota?.source && detail.customQuota.source !== "MANUAL" && (
                    <Alert severity="info">{t("superAdmin.customers.migrated", { source: detail.customQuota.source })}</Alert>
                  )}
                  <AppCheckbox
                    label={t("superAdmin.customers.unlimited")}
                    checked={quotaUnlimited}
                    onChange={(_, checked) => setQuotaUnlimited(checked)}
                  />
                  {!quotaUnlimited && (
                    <QuotaFieldsEditor
                      form={quotaForm}
                      onChange={(key, value) => setQuotaForm((f) => ({ ...f, [key]: value }))}
                      emptyHint={t("superAdmin.customers.sameAsFree")}
                    />
                  )}
                  <AppFormGrid>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField type="date" label={t("superAdmin.customers.validFrom")} value={quotaFrom} onChange={(e) => setQuotaFrom(e.target.value)} InputLabelProps={{ shrink: true }} helperText={t("superAdmin.customers.validFromHelp")} />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField type="date" label={t("superAdmin.customers.validUntil")} value={quotaUntil} onChange={(e) => setQuotaUntil(e.target.value)} InputLabelProps={{ shrink: true }} helperText={t("superAdmin.customers.validUntilHelp")} />
                    </AppFormField>
                    <AppFormField size={GRID_FULL}>
                      <AppTextField label={t("superAdmin.customers.note")} value={quotaNote} onChange={(e) => setQuotaNote(e.target.value)} multiline minRows={2} fullWidth />
                    </AppFormField>
                  </AppFormGrid>
                  <Stack direction="row" spacing={1}>
                    <AppButton onClick={() => void saveQuota()} disabled={saving} loading={saving}>
                      {t("superAdmin.customers.saveQuota")}
                    </AppButton>
                    {detail.customQuota && (
                      <AppButton variant="outlined" color="error" onClick={() => void removeQuota()} disabled={saving}>
                        {t("superAdmin.customers.removeQuota")}
                      </AppButton>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            )}

            {tab === "support" && (
              <Stack spacing={3}>
                {detail.support.length === 0 ? (
                  <Alert severity="info">{t("superAdmin.customers.noSupport")}</Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {detail.support.map((e) => (
                      <Box key={e.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="subtitle2" fontWeight={650}>
                                {e.packageName}
                              </Typography>
                              <Chip size="small" variant="outlined" color={e.usable ? "success" : "default"} label={t(`workspaceSupport.status.${e.usable ? "ACTIVE" : e.status}`)} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {fmtDate(e.startsAt)} – {fmtDate(e.expiresAt)} · {remainingText(e, t)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t("superAdmin.customers.paymentsLine", {
                                count: e.payments?.length ?? 0,
                                total: (e.payments ?? []).reduce((a, p) => a + Number(p.amount || 0), 0).toLocaleString(),
                              })}
                            </Typography>
                          </Box>
                          {e.status !== "CANCELLED" && (
                            <Stack direction="row" spacing={0.25}>
                              <AppIconButton size="small" title={t("superAdmin.customers.recordUsage")} disabled={!e.usable} onClick={() => {
                                setUsageFor(e);
                                setUsageKind(e.tickets != null ? "TICKET" : "MEETING");
                                setUsageAmount(e.tickets != null ? "1" : "30");
                                setUsageRef("");
                                setUsageNote("");
                              }}>
                                <PlaylistAddCheckOutlinedIcon fontSize="small" />
                              </AppIconButton>
                              <AppIconButton size="small" title={t("superAdmin.customers.recordPayment")} onClick={() => {
                                setPaymentFor(e);
                                setInvoiceNo("");
                                setAmount(String(Number(e.priceEur || 0) + Number(e.installFeeEur || 0) || ""));
                              }}>
                                <PaymentsOutlinedIcon fontSize="small" />
                              </AppIconButton>
                              <AppIconButton size="small" color="error" title={t("superAdmin.customers.cancelSupport")} onClick={() => void cancelSupport(e)}>
                                <BlockOutlinedIcon fontSize="small" />
                              </AppIconButton>
                            </Stack>
                          )}
                        </Stack>
                        {(e.usage?.length ?? 0) > 0 && (
                          <Table size="small" sx={{ mt: 1 }}>
                            <TableBody>
                              {e.usage!.slice().reverse().map((u) => (
                                <TableRow key={u.id}>
                                  <TableCell>{fmtDate(u.at)}</TableCell>
                                  <TableCell>{t(`superAdmin.customers.usageKind.${u.kind}`, { amount: u.amount })}</TableCell>
                                  <TableCell>{u.reference || "—"}</TableCell>
                                  <TableCell>{u.recordedBy || "—"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
                <Divider />
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={650}>
                    {t("superAdmin.customers.assignSupport")}
                  </Typography>
                  <AppFormGrid>
                    <AppFormField size={GRID_HALF}>
                      <AppAutocomplete<{ id: string; name: string }>
                        label={t("superAdmin.customers.package")}
                        options={packageOptions}
                        value={packageOptions.find((o) => o.id === assignKey) ?? null}
                        getOptionLabel={(o) => o.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_, v) => setAssignKey(v?.id ?? "")}
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField type="date" label={t("superAdmin.customers.startsAt")} value={assignStart} onChange={(e) => setAssignStart(e.target.value)} InputLabelProps={{ shrink: true }} helperText={t("superAdmin.customers.startsAtHelp")} />
                    </AppFormField>
                    {isCustom && (
                      <>
                        <AppFormField size={GRID_FULL}>
                          <AppTextField label={t("superAdmin.support.name")} value={custom.name} onChange={(e) => setCustom((x) => ({ ...x, name: e.target.value }))} fullWidth />
                        </AppFormField>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField type="number" label={t("superAdmin.support.tickets")} value={custom.tickets} onChange={(e) => setCustom((x) => ({ ...x, tickets: e.target.value }))} />
                        </AppFormField>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField type="number" label={t("superAdmin.support.meetingHours")} value={custom.hours} onChange={(e) => setCustom((x) => ({ ...x, hours: e.target.value }))} />
                        </AppFormField>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField type="number" label={t("superAdmin.support.priceEur")} value={custom.price} onChange={(e) => setCustom((x) => ({ ...x, price: e.target.value }))} />
                        </AppFormField>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField type="number" label={t("superAdmin.support.installFeeEur")} value={custom.install} onChange={(e) => setCustom((x) => ({ ...x, install: e.target.value }))} />
                        </AppFormField>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField label={t("superAdmin.support.ticketResponse")} value={custom.ticketResponse} onChange={(e) => setCustom((x) => ({ ...x, ticketResponse: e.target.value }))} />
                        </AppFormField>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField label={t("superAdmin.support.meetingResponse")} value={custom.meetingResponse} onChange={(e) => setCustom((x) => ({ ...x, meetingResponse: e.target.value }))} />
                        </AppFormField>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField type="number" label={t("superAdmin.support.termMonths")} value={custom.term} onChange={(e) => setCustom((x) => ({ ...x, term: e.target.value }))} />
                        </AppFormField>
                      </>
                    )}
                    <AppFormField size={GRID_FULL}>
                      <AppTextField label={t("superAdmin.customers.note")} value={assignNote} onChange={(e) => setAssignNote(e.target.value)} fullWidth />
                    </AppFormField>
                  </AppFormGrid>
                  <Box>
                    <AppButton startIcon={<AddOutlinedIcon />} disabled={!assignKey || saving} loading={saving} onClick={() => void assign()}>
                      {t("superAdmin.customers.assign")}
                    </AppButton>
                  </Box>
                </Stack>
              </Stack>
            )}

            {tab === "history" && (
              detail.legacySubscriptions.length === 0 ? (
                <Alert severity="info">{t("superAdmin.customers.noHistory")}</Alert>
              ) : (
                <Stack spacing={1.5}>
                  <Alert severity="info">{t("superAdmin.customers.historyHint")}</Alert>
                  {detail.legacySubscriptions.map((s) => (
                    <Box key={s.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="subtitle2">
                        {s.customerType} · {s.paymentStatus} · {s.username || s.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {fmtDate(s.activationDate)} – {fmtDate(s.deactivationDate)}
                        {s.migratedAt ? ` · ${t("superAdmin.customers.migratedAt", { date: fmtDate(s.migratedAt) })}` : ""}
                      </Typography>
                      {(s.payments?.length ?? 0) > 0 && (
                        <Table size="small" sx={{ mt: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("superAdmin.customers.invoiceNo")}</TableCell>
                              <TableCell>{t("superAdmin.customers.amount")}</TableCell>
                              <TableCell>{t("superAdmin.customers.paidAt")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {s.payments!.map((p) => (
                              <TableRow key={p.id || p.invoiceNo}>
                                <TableCell>{p.invoiceNo}</TableCell>
                                <TableCell>
                                  {p.amount} {p.currency || "EUR"}
                                </TableCell>
                                <TableCell>{fmtDate(p.paidAt)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  ))}
                </Stack>
              )
            )}
          </Stack>
        ) : null}
      </AppSlideOver>

      <Dialog open={!!usageFor} onClose={() => !saving && setUsageFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t("superAdmin.customers.recordUsage")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {usageFor && <Typography variant="body2">{remainingText(usageFor, t)}</Typography>}
            <AppAutocomplete<{ id: "TICKET" | "MEETING"; name: string }>
              label={t("superAdmin.customers.usageType")}
              options={[
                ...(usageFor?.tickets != null ? [{ id: "TICKET" as const, name: t("superAdmin.customers.usageTicket") }] : []),
                ...(usageFor?.meetingHours != null ? [{ id: "MEETING" as const, name: t("superAdmin.customers.usageMeeting") }] : []),
              ]}
              value={{ id: usageKind, name: t(usageKind === "TICKET" ? "superAdmin.customers.usageTicket" : "superAdmin.customers.usageMeeting") }}
              disableClearable
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => v && setUsageKind(v.id)}
            />
            <AppTextField
              type="number"
              label={t(usageKind === "TICKET" ? "superAdmin.customers.usageTickets" : "superAdmin.customers.usageMinutes")}
              value={usageAmount}
              onChange={(e) => setUsageAmount(e.target.value)}
              inputProps={{ min: 1 }}
            />
            <AppTextField label={t("superAdmin.customers.reference")} value={usageRef} onChange={(e) => setUsageRef(e.target.value)} />
            <AppTextField label={t("superAdmin.customers.note")} value={usageNote} onChange={(e) => setUsageNote(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <AppButton variant="outlined" onClick={() => setUsageFor(null)} disabled={saving}>
            {t("common.cancel")}
          </AppButton>
          <AppButton onClick={() => void recordUsage()} disabled={saving} loading={saving}>
            {t("common.save")}
          </AppButton>
        </DialogActions>
      </Dialog>

      <Dialog open={!!paymentFor} onClose={() => !saving && setPaymentFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t("superAdmin.customers.recordPayment")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <AppTextField label={t("superAdmin.customers.invoiceNo")} value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
            <AppTextField type="number" label={t("superAdmin.customers.amount")} value={amount} onChange={(e) => setAmount(e.target.value)} inputProps={{ min: 0, step: "0.01" }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <AppButton variant="outlined" onClick={() => setPaymentFor(null)} disabled={saving}>
            {t("common.cancel")}
          </AppButton>
          <AppButton onClick={() => void recordPayment()} disabled={saving || !invoiceNo.trim() || !(Number(amount) > 0)} loading={saving}>
            {t("common.save")}
          </AppButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
